/* eslint-disable camelcase */
// Recharge does not use camelcase
import { useDisclosure, useToast } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { useEffectOnce } from 'react-use';
import { billingCycleMapping } from '@rsc/ui-components/dept/organisms/Account/utils';
import {
  initRecharge,
  listSubscriptions,
  loginWithShopifyStorefront,
  skipSubscriptionCharge,
  skipCharge,
  listCharges,
  cancelSubscription as cancelRechargeSubscription,
  updateSubscription,
  listAddresses,
  createAddress,
  updateSubscriptionAddress,
  getCustomerPortalAccess,
} from '@rechargeapps/storefront-client';
import { CustomerContext } from './CustomerContext';

const SubscriptionsContext = createContext();

const isBrowser = typeof window !== 'undefined';

const SEARCH_PRODUCTS_QUERY = `
  query searchProducts($searchQuery: String!) {
    search(query: $searchQuery, first: 250) {
      nodes {
        ... on Product {
          variants(first: 50) {
            nodes {
              id
              sku
              image {
                url(transform: { maxWidth: 400 })
              }
              selectedOptions {
                name
                value
              }
            }
          }
          sellingPlanGroups(first: 1) {
            nodes {
              sellingPlans(first: 15) {
                nodes {
                  id
                  name
                }
              }
            }
          }
          featuredImage {
            url
          }
          title
          handle
        }
      }
    }
  }
`;

const CREATE_CART = `
mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
        cart {
            buyerIdentity {
              email
            }
            checkoutUrl
        }
        userErrors {
            field
            message
        }
    }
}
`;

const DEFAULT_SELLING_PLANS = [
  {
    id: 'gid://shopify/SellingPlan/1146486843',
    name: 'Delivery every 1 Month',
  },
  {
    id: 'gid://shopify/SellingPlan/1146519611',
    name: 'Delivery every 2 Months',
  },
  {
    id: 'gid://shopify/SellingPlan/1146552379',
    name: 'Delivery every 3 Months',
  },
  {
    id: 'gid://shopify/SellingPlan/1146585147',
    name: 'Delivery every 4 Months',
  },
  {
    id: 'gid://shopify/SellingPlan/1146617915',
    name: 'Delivery every 6 Months',
  },
  {
    id: 'gid://shopify/SellingPlan/1146650683',
    name: 'Delivery every 12 Months',
  },
];

const EXCEPTION_SKUS = {
  'RFA-18-11': {
    slug: '6-volt-alkaline-battery',
    shopifyId: '7154834014347',
    title: '6-Volt Alkaline Battery',
    sellingPlans: DEFAULT_SELLING_PLANS,
  },
  'PAC00-16467': {
    slug: 'drinkwell-replacement-foam-filters',
    shopifyId: '7154860327051',
    title: 'Drinkwell® Replacement Foam Filters (2-Pack)',
    sellingPlans: DEFAULT_SELLING_PLANS,
  },
  'ZAC00-15905': {
    slug: 'piddle-place-pet-potty-bio-concentrate-turf-treatment',
    shopifyId: '7154588123275',
    title: 'Piddle Place™ Pet Potty Bio+ Concentrate (9-Pack) & Turf Treatment',
    sellingPlans: DEFAULT_SELLING_PLANS,
  },
};

function SubscriptionsProvider({ children }) {
  const customerContext = useContext(CustomerContext);
  const rechargeSessionRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [legacySubscriptions, setLegacySubscriptions] = useState([]);
  const [subscriptionProducts, setSubscriptionProducts] = useState([]);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState([]);
  const [modalDismissed, setModalDismissed] = useState(false);
  const [rechargeSubscriptions, setRechargeSubscriptions] = useState([]);
  const [rechargeCharges, setRechargeCharges] = useState([]);
  const [rechargeAddresses, setRechargeAddresses] = useState([]);

  const toast = useToast();
  const showToast = message =>
    toast({
      ...message,
      duration: 9000,
      isClosable: true,
    });
  const errorToast = () =>
    showToast({
      title: 'Something went wrong',
      description:
        'Please refresh the page and try again. If the error persists, please reach out to our customer care team for further assistance.',
      status: 'error',
    });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const openModal = e => {
    e.preventDefault();
    e.stopPropagation();
    setModalDismissed(false);
    onOpen(e);
  };

  const closeModal = () => {
    setModalDismissed(true);
    onClose();
  };

  const searchShopifyProducts = async (storefrontClient, skus) => {
    const searchQuery = skus.map(sku => `variants.sku:'${sku}'`).join(' OR ');

    const data = await storefrontClient.request(SEARCH_PRODUCTS_QUERY, {
      searchQuery,
    });

    setSubscriptionProducts(data?.search?.nodes);
  };

  const fetchSubscriptions = async () => {
    setLoading(true);
    if (isBrowser) {
      const { shopifyClient, getMagentoSubscriptions, checkCognitoIdExpiry } =
        customerContext;
      await checkCognitoIdExpiry();
      const magentoSubscriptions = await getMagentoSubscriptions();
      setLegacySubscriptions(magentoSubscriptions);

      const skus = magentoSubscriptions.map(s => s?.sku);
      await searchShopifyProducts(shopifyClient, skus);
    }
    setLoading(false);
  };

  useEffectOnce(() => {
    (async () => {
      await fetchSubscriptions();
    })();
  });

  const handleCheckboxChange = sub => {
    if (
      selectedSubscriptions.some(
        selected => selected.magentoSubscriptionID === sub.magentoSubscriptionID
      )
    ) {
      setSelectedSubscriptions(
        selectedSubscriptions.filter(
          selected =>
            selected.magentoSubscriptionID !== sub.magentoSubscriptionID
        )
      );
    } else {
      setSelectedSubscriptions([...selectedSubscriptions, sub]);
    }
  };

  const handleCancelClick = async id => {
    setLoading(true);
    try {
      if (isBrowser) {
        const token = window.localStorage.getItem('cognito_id_token');
        await fetch(
          `${process.env.GATSBY_GET_SUBSCRIPTIONS_ENDPOINT}?id=${id}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSelectedSubscriptions([]);
        await fetchSubscriptions();
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const getCheckoutUrl = async () => {
    setLoading(true);
    try {
      const { shopifyClient, customer } = customerContext;
      const shopifyLines = selectedSubscriptions.map(subscription => {
        if (!subscription.variant?.id) {
          console.info(
            'Missing Shopify Product Variant for',
            subscription?.sku
          );
          return '';
        }

        const sellingPlans = subscription.sellingPlans || [];

        const selectedFrequencyInMonths =
          isBrowser &&
          document?.querySelector(
            `#sub-${subscription?.magentoSubscriptionID}-frequency`
          )?.selectedOptions?.[0]?.value;

        const frequencyInMonths =
          parseInt(selectedFrequencyInMonths, 10) ||
          billingCycleMapping(subscription?.billingCycle);

        const sellingPlan = sellingPlans?.find(sp => {
          const extractedNumber = sp.name.match(/\d+/);
          const frequency = extractedNumber
            ? parseInt(extractedNumber[0], 10)
            : 0;
          return frequency === frequencyInMonths;
        });

        if (!sellingPlan?.id) {
          throw new Error(
            'Missing Shopify Selling Plan ID for',
            subscription?.sku
          );
        }

        return {
          merchandiseId: subscription.variant.id,
          quantity: Number.parseInt(subscription?.lineItemQuantity, 10),
          sellingPlanId: sellingPlan.id,
          attributes: [
            {
              key: 'Magento Subscription ID',
              value: parseInt(
                subscription?.magentoSubscriptionID,
                10
              ).toString(),
            },
          ],
        };
      });

      const buyerIdentity = {
        countryCode: 'US',
        email: customer?.email,
      };

      const customerAccessToken =
        isBrowser && window.localStorage.getItem('customer_access_token');

      const customerAddress = customer?.defaultAddress;

      if (customerAccessToken) {
        buyerIdentity.customerAccessToken = customerAccessToken;
      }

      if (customerAddress?.id) {
        buyerIdentity.deliveryAddressPreferences = {
          customerAddressId: customerAddress.id,
        };
      }

      const cart = await shopifyClient.request(CREATE_CART, {
        input: {
          buyerIdentity,
          lines: shopifyLines.filter(l => l.merchandiseId),
        },
      });

      if (!cart?.cartCreate?.cart?.checkoutUrl) {
        throw new Error('Missing cart', cart);
      }

      window.location.href = cart.cartCreate.cart.checkoutUrl;
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const pendingLegacySubscriptions = useMemo(
    () =>
      legacySubscriptions
        .filter(sub => sub.subscriptionStatus === 'Pending')
        .map(sub => {
          if (subscriptionProducts) {
            const product = subscriptionProducts.find(sp =>
              sp.variants.nodes.some(v => v.sku === sub.sku)
            );

            if (product) {
              const {
                title,
                handle,
                variants,
                featuredImage,
                sellingPlanGroups,
              } = product;
              const variant = variants?.nodes?.[0];

              const imageUrl =
                variant?.image?.url ?? featuredImage?.url ?? 'blank';

              const sellingPlans =
                sellingPlanGroups?.nodes?.[0]?.sellingPlans?.nodes;

              return {
                ...sub,
                title,
                handle,
                sellingPlans,
                variant,
                imageUrl,
              };
            }

            if (EXCEPTION_SKUS[sub.sku]) {
              const { title, sellingPlans } = EXCEPTION_SKUS[sub.sku];

              return {
                ...sub,
                title,
                sellingPlans,
                archivedProduct: true,
              };
            }
          }
          return sub;
        }),
    [legacySubscriptions, subscriptionProducts]
  );

  useEffect(() => {
    setSelectedSubscriptions(
      pendingLegacySubscriptions?.filter(pls => !pls?.archivedProduct)
    );
  }, [pendingLegacySubscriptions]);

  useEffectOnce(() => {
    onOpen();
  });

  const getRechargeSession = async () => {
    if (isBrowser) {
      const customerAccessToken = window.localStorage.getItem(
        'customer_access_token'
      );

      if (customerAccessToken) {
        const session = await loginWithShopifyStorefront(
          process.env.GATSBY_SHOPIFY_CUSTOMER_STOREFRONT_TOKEN,
          customerAccessToken
        );
        return session;
      }
    }
    return null;
  };

  const initializeRecharge = async () => {
    if (!rechargeSessionRef?.current) {
      try {
        initRecharge({
          storeIdentifier: `${process.env.GATSBY_SHOPIFY_SHOP_NAME}.myshopify.com`,
          storefrontAccessToken: process.env.GATSBY_RECHARGE_API_KEY,
          loginRetryFn: await getRechargeSession,
        });
        rechargeSessionRef.current = true;
      } catch (err) {
        console.error(`err =>`, err);
      }
    }
  };

  const getRechargeCharges = async () => {
    if (!rechargeSessionRef?.current) {
      await initializeRecharge();
    }

    if (isBrowser) {
      const session = await getRechargeSession();
      if (session?.customerId) {
        const res = await listCharges(session, {
          limit: 25,
          sort_by: 'created_at-asc',
        });

        setRechargeCharges(res.charges);
      }
    }
  };

  const getRechargeAddresses = async () => {
    if (!rechargeSessionRef?.current) {
      await initializeRecharge();
    }

    if (isBrowser) {
      const session = await getRechargeSession();
      if (session?.customerId) {
        const res = await listAddresses(session, {
          limit: 25,
        });
        setRechargeAddresses(res.addresses);
      }
    }
  };

  const getRechargeSubscriptions = async () => {
    if (!rechargeSessionRef?.current) {
      await initializeRecharge();
    }

    if (isBrowser) {
      const session = await getRechargeSession();
      if (session?.customerId) {
        const { addresses } = await listAddresses(session, {
          limit: 25,
        });
        const customerSubRes = await listSubscriptions(session, {
          limit: 25,
          sort_by: 'created_at-asc',
        });

        const skus = [
          ...new Set(customerSubRes.subscriptions.map(({ sku }) => sku)),
        ];

        const searchQuery = skus
          .map(sku => `variants.sku:'${sku}'`)
          .join(' OR ');

        const data = await customerContext?.shopifyClient.request(
          SEARCH_PRODUCTS_QUERY,
          {
            searchQuery,
          }
        );

        const subs = customerSubRes.subscriptions?.map(sub => {
          const shopifyProduct = data.search.nodes.find(p =>
            p.variants?.nodes?.some(({ sku }) => sku === sub.sku)
          );
          const variant = shopifyProduct?.variants?.nodes?.[0];

          const sellingPlans =
            shopifyProduct?.sellingPlanGroups?.nodes?.[0]?.sellingPlans?.nodes;

          const imageUrl =
            variant?.image?.url ??
            shopifyProduct?.featuredImage?.url ??
            'blank';

          return {
            ...sub,
            imageUrl,
            shopifyProduct,
            sellingPlans,
            address: addresses.find(a => a.id === sub.address_id),
          };
        });

        setRechargeSubscriptions(subs);
      }
    }

    return null;
  };

  const skipNextDelivery = async (chargeId, itemIds) => {
    if (isBrowser) {
      const customerAccessToken = window.localStorage.getItem(
        'customer_access_token'
      );

      if (customerAccessToken) {
        try {
          const session = await getRechargeSession();
          await skipSubscriptionCharge(session, chargeId, itemIds);
          await getRechargeSubscriptions();

          showToast({
            title: 'Next Delivery has been skipped',
            description:
              'If this was an error, please call our customer care team to help.',
            status: 'success',
          });
        } catch (err) {
          console.error(`err =>`, err);
          errorToast();
        }
      }
    }
  };

  const skipNextCharge = async (chargeId, itemIds) => {
    if (isBrowser) {
      const customerAccessToken = window.localStorage.getItem(
        'customer_access_token'
      );

      if (customerAccessToken) {
        try {
          const session = await getRechargeSession();
          await skipCharge(session, chargeId, itemIds);
          await getRechargeSubscriptions();

          showToast({
            title: 'Next Delivery has been skipped',
            description:
              'If this was an error, please call our customer care team to help.',
            status: 'success',
          });
        } catch (err) {
          console.error(`err =>`, err);
          errorToast();
        }
      }
    }
  };

  const cancelSubscription = async (subId, reason, comments) => {
    if (isBrowser) {
      const customerAccessToken = window.localStorage.getItem(
        'customer_access_token'
      );

      if (customerAccessToken) {
        try {
          const session = await getRechargeSession();
          await cancelRechargeSubscription(session, subId, {
            cancellation_reason: reason,
            cancellation_comments: comments,
            send_email: true,
          });
          await getRechargeSubscriptions();

          showToast({
            title: `You're subscription has been cancelled`,
            description:
              'If this was an error, please call our customer care team to help.',
            status: 'success',
          });
        } catch (err) {
          console.error(`err =>`, err);
          errorToast();
        }
      }
    }
  };

  const manageSubscription = async ({
    subId,
    quantity,
    frequency,
    orderUnit,
    addressId,
  }) => {
    if (isBrowser) {
      const customerAccessToken = window.localStorage.getItem(
        'customer_access_token'
      );

      if (customerAccessToken) {
        try {
          const session = await getRechargeSession();

          if (addressId) {
            await updateSubscriptionAddress(session, subId, addressId);
          }

          if (quantity || frequency) {
            await updateSubscription(session, subId, {
              quantity,
              charge_interval_frequency: frequency,
              order_interval_unit: orderUnit,
              order_interval_frequency: frequency,
              is_prepaid: false,
            });
          }

          await getRechargeSubscriptions();

          showToast({
            title: `You're subscription has been updated!`,
            status: 'success',
          });
        } catch (err) {
          console.error(`err =>`, err);
          errorToast();
        }
      }
    }
  };

  const createRechargeAddress = async ({
    customer_id,
    address1 = '',
    address2,
    city,
    country_code = 'US',
    first_name,
    last_name = '',
    phone = '',
    province,
    zip,
  }) => {
    if (isBrowser) {
      const customerAccessToken = window.localStorage.getItem(
        'customer_access_token'
      );

      if (customerAccessToken) {
        try {
          const session = await getRechargeSession();
          const address = await createAddress(session, {
            customer_id,
            address1,
            address2,
            city,
            first_name,
            last_name,
            phone,
            province,
            zip,
            country_code,
          });
          await getRechargeAddresses();
          return address.id;
        } catch (err) {
          console.error(`err =>`, err);
        }
      }
    }
    return null;
  };

  const getRechargePortalAccess = async () => {
    if (isBrowser) {
      const customerAccessToken = window.localStorage.getItem(
        'customer_access_token'
      );

      if (customerAccessToken) {
        try {
          const session = await getRechargeSession();
          if (session?.customer_id || session?.customerId) {
            const portalData = await getCustomerPortalAccess(session);
            return portalData;
          }
        } catch (err) {
          console.error(`err =>`, err);
        }
      }
    }
    return null;
  };

  useEffect(() => {
    (async () => {
      await getRechargeSubscriptions();
      await getRechargeCharges();
      await getRechargeAddresses();
    })();
  }, [customerContext?.customer]);

  const memoizedValue = useMemo(
    () => ({
      pendingLegacySubscriptions,
      legacySubscriptions,
      userHasPendingLegacySubscriptions: !!legacySubscriptions?.length,
      loadingLegacySubscriptions: loading,
      fetchSubscriptions,
      getCheckoutUrl,
      selectedSubscriptions,
      handleCheckboxChange,
      billingCycleMapping,
      handleCancelClick,
      openModal,
      closeModal,
      isOpen,
      modalDismissed,
      isAuthenticated: customerContext?.customer?.isAuthenticated,
      rechargeSubscriptions,
      rechargeCharges,
      rechargeAddresses,
      skipNextCharge,
      skipNextDelivery,
      cancelSubscription,
      manageSubscription,
      createRechargeAddress,
      getRechargePortalAccess,
    }),
    [
      legacySubscriptions,
      subscriptionProducts,
      selectedSubscriptions,
      loading,
      isOpen,
      modalDismissed,
      customerContext,
      rechargeSubscriptions,
      rechargeCharges,
      rechargeAddresses,
    ]
  );

  return (
    <SubscriptionsContext.Provider value={memoizedValue}>
      {children}
    </SubscriptionsContext.Provider>
  );
}

SubscriptionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { SubscriptionsContext, SubscriptionsProvider };
